import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useEnterShop } from "@/lib/useShop";
import { btnPrimary, money } from "@/components/simple/product/Fields";
import { Lines } from "@/components/track/Tracker";
import { useReorder } from "@/components/track/useReorder";

const thumb = (it) => it.proof?.url || (it.artwork || []).find((a) => a.url)?.url || null;
const longDate = (iso) => { try { return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); } catch { return iso; } };

/** `/order-confirmation/:id` in Simple (7.8): code, big Ready by, pickup or shipping, the lines, Track my order. */
export default function SimpleConfirmation() {
    const { id } = useParams();
    const settings = useSettings();
    const [order, setOrder] = useState(null);
    const [shop, setShop] = useState(null);
    const [missing, setMissing] = useState(false);
    const { reorder, busy } = useReorder();
    useEnterShop(shop?.slug && shop.slug !== "13th-ave-sign-and-shirt" ? shop.slug : null);

    useEffect(() => {
        api.get(`/orders/${id}`).then(({ data }) => {
            setOrder(data);
            const slug = data.items?.[0]?.seller_slug || data.card_store || data.items?.[0]?.store_slug;
            if (slug) api.get(`/stores/${slug}`).then((r) => setShop(r.data.store || {})).catch(() => setShop({}));
        }).catch(() => setMissing(true));
    }, [id]);

    if (missing) {
        return (
            <Layout title="Order confirmed">
                <section className="mx-auto w-full max-w-4xl px-3 py-4 sm:px-6" data-testid="confirmation-missing">
                    <h1 className="text-[28px] font-semibold leading-tight">We can't find that order.</h1>
                    <Link to="/" className={`${btnPrimary} mt-4`} data-testid="confirmation-home">Home</Link>
                    <Link to="/track" className="mt-3 flex min-h-[44px] items-center text-[16px] underline" data-testid="confirmation-track-link">Track an order</Link>
                </section>
            </Layout>
        );
    }
    if (!order) return <Layout title="Order confirmed"><p className="mx-auto w-full max-w-4xl px-3 py-6 text-muted-ink sm:px-6" data-testid="confirmation-loading">Loading…</p></Layout>;

    const paid = order.card_paid || ["paid", "in_production", "ready", "completed"].includes(order.status);
    const supplied = order.items?.some((i) => i.customer_supplied || i.selections?.source === "supplied");
    const ship = order.fulfillment === "ship";
    const isPayment = order.order_type && order.order_type !== "print";
    const steps = settings?.business?.dropoff?.steps || [];
    const items = (order.items || []).map((i) => ({ ...i, image: thumb(i) }));
    const locker = shop ? (shop.pickup_mode || "locker") === "locker" : true;

    return (
        <Layout title="Order confirmed">
            <section className="mx-auto w-full max-w-4xl px-3 py-4 sm:px-6" data-testid="simple-confirmation">
                <p className="text-[16px] text-muted-ink">{paid ? "Paid" : "Order received"}</p>
                <h1 className="text-[28px] font-semibold leading-tight">Order <span data-testid="order-number">{order.order_number}</span></h1>
                {isPayment ? (
                    <p className="mt-3 text-[20px] font-semibold" data-testid="payment-line">{money(order.total)} received{order.linked_order_number ? ` for order ${order.linked_order_number}` : ""}.</p>
                ) : order.ready_by && (
                    <p className="mt-3 text-[28px] font-semibold leading-tight" data-testid="ready-by-big">Ready by {longDate(order.ready_by)}</p>
                )}
                {!isPayment && (
                    <div className="mt-4 border border-ink px-3 py-3 text-[16px]" data-testid={ship ? "shipping-block" : "pickup-block"}>
                        {ship ? (
                            <>
                                <p className="font-semibold">Shipping</p>
                                <p className="mt-1">{order.shipping?.address ? [order.shipping.name, order.shipping.address.line1, order.shipping.address.line2, `${order.shipping.address.city || ""} ${order.shipping.address.state || ""} ${order.shipping.address.postal_code || ""}`].filter(Boolean).join(", ") : "We'll text you the tracking link when it ships."}</p>
                            </>
                        ) : (
                            <>
                                <p className="font-semibold">Pickup</p>
                                {shop?.pickup_address && <p className="mt-1" data-testid="pickup-address">{shop.pickup_address}</p>}
                                <p className="mt-1 text-muted-ink">{locker ? "24-hour locker. The pickup code is texted to you when it's ready." : "We'll text you when it's ready to pick up."}</p>
                            </>
                        )}
                    </div>
                )}
                {supplied && (
                    <div className="mt-4 border border-ink px-3 py-3 text-[16px]" data-testid="dropoff-steps">
                        <p className="font-semibold">Dropping off your own apparel</p>
                        <ol className="mt-1 list-decimal space-y-1 pl-5">
                            {steps.map((s, i) => (
                                <li key={i}>{order.dropoff_code && /drop-off code/i.test(s)
                                    ? <>Enter the drop-off code: <span className="font-semibold" data-testid="dropoff-code">{order.dropoff_code}</span></>
                                    : s}</li>
                            ))}
                            {!steps.length && order.dropoff_code && <li>Drop-off code: <span className="font-semibold" data-testid="dropoff-code">{order.dropoff_code}</span></li>}
                        </ol>
                    </div>
                )}
                <div className="mt-4"><Lines items={items} /></div>
                <p className="mt-2 flex justify-between text-[16px]"><span>{paid ? "Paid" : "Total"}</span><span className="font-semibold" data-testid="order-total">{money(order.paid_amount || order.total)}</span></p>
                {order.proof_token && order.items?.some((i) => i.proof) && order.proof_status === "pending" && !order.proof_skipped && (
                    <p className="mt-2 text-[16px]" data-testid="proof-banner">We emailed your proof. <Link to={`/proof/${order.proof_token}`} className="underline" data-testid="proof-link">Approve it here</Link>.</p>
                )}
                <Link to={`/track/${order.order_number}`} className={`${btnPrimary} mt-4`} data-testid="confirmation-track">Track my order</Link>
                <div className="mt-2 flex flex-wrap gap-4 text-[16px]">
                    {!isPayment && (
                        <button type="button" onClick={() => reorder({ id: order.id, token: order.reorder_token })} disabled={busy} className="flex min-h-[44px] items-center underline" data-testid="confirmation-reorder">
                            {busy ? "Working…" : "Reorder"}
                        </button>
                    )}
                    <Link to={shop?.slug && shop.slug !== "13th-ave-sign-and-shirt" ? `/stores/${shop.slug}` : "/"} className="flex min-h-[44px] items-center underline" data-testid="confirmation-keep-shopping">Keep shopping</Link>
                </div>
            </section>
        </Layout>
    );
}
